import { assistedTokens } from './assisted-match.js';

/**
 * Ningún pedazo de un repaso pasa de estas palabras (card #580, Emmanuel 21/10:
 * «el repaso es para trabajar algo puntual que no te salió, no para ver si podés
 * traducir de memoria un párrafo entero»). Vale en todos: del día, del cierre,
 * de la partida, del Radar, «Decilas ahora» y las palabras guardadas. Lo hace
 * cumplir el server, recortando la oración (`apps/backend/src/lib/review-piece.ts`).
 */
export const REVIEW_PHRASE_MAX_WORDS = 12;

/**
 * La oración entera de una CORRECCIÓN se repasa hasta estas palabras (card #580,
 * Emmanuel 21/10: «esas frases no me parecen imposibles de decir, más si vienen de lo
 * que dijo el mismo alumno»). Más larga, se recorta al pedazo del error, de
 * `REVIEW_PHRASE_MAX_WORDS` como mucho. Los ejemplos del diccionario no: tope de 12.
 * Medido sobre la base local (63 oraciones de más de 12 palabras): con 18 quedan enteras
 * las de hasta 18 y salen 4 recortes rotos menos que con 16; una de 18 palabras (95
 * letras) entra en la tarjeta a 390×664 igual que una de 16.
 */
export const REVIEW_SENTENCE_MAX_WORDS = 18;

/**
 * El repaso junta las correcciones de un MISMO turno que se pisan (una frase
 * corregida contiene a la otra; Emmanuel, 15/10). De «Probably it would be
 * being closer from my family.» el corrector sacó «being closer to my family»
 * y «it would be being closer to my family», que sólo completa la frase dicha
 * con la otra corrección: el repaso pedía casi lo mismo dos veces.
 *
 * De cada grupo se repasa UNA (la primera del grupo devuelto) y lo decidido
 * vale para todas. Queda la que corrige algo de verdad: una que sólo agrega
 * palabras alrededor de lo dicho («completa») cede ante una que cambia lo
 * dicho; entre las que quedan, la más larga, que abarca a las otras. Sin turno
 * (`turnOf` → `null`), nunca se junta.
 */
export function groupOverlappingCorrections<T extends { said: string; corrected: string }>(
  items: readonly T[],
  turnOf: (item: T) => string | null,
): T[][] {
  const tokens = items.map((it) => ({
    said: assistedTokens(it.said),
    corrected: assistedTokens(it.corrected),
  }));
  const parent = items.map((_, i) => i);
  const find = (i: number): number => {
    while (parent[i] !== i) i = parent[i] = parent[parent[i]!]!;
    return i;
  };
  for (let i = 0; i < items.length; i++) {
    const ti = turnOf(items[i]!);
    if (ti === null) continue;
    for (let j = i + 1; j < items.length; j++) {
      if (turnOf(items[j]!) !== ti) continue;
      const a = tokens[i]!.corrected;
      const b = tokens[j]!.corrected;
      if (containsRun(a, b) || containsRun(b, a)) parent[find(j)] = find(i);
    }
  }
  const groups = new Map<number, number[]>();
  for (let i = 0; i < items.length; i++) {
    const root = find(i);
    const g = groups.get(root);
    if (g) g.push(i);
    else groups.set(root, [i]);
  }
  return [...groups.values()].map((members) => {
    const completes = (i: number): boolean =>
      tokens[i]!.corrected.length > tokens[i]!.said.length &&
      containsRun(tokens[i]!.corrected, tokens[i]!.said);
    const real = members.filter((i) => !completes(i));
    const candidates = real.length > 0 ? real : members;
    let keep = candidates[0]!;
    for (const i of candidates) {
      if (tokens[i]!.corrected.length > tokens[keep]!.corrected.length) keep = i;
    }
    return [keep, ...members.filter((i) => i !== keep)].map((i) => items[i]!);
  });
}

/** ¿`run` aparece entera y seguida dentro de `seq`? */
function containsRun(seq: readonly string[], run: readonly string[]): boolean {
  if (run.length === 0 || run.length > seq.length) return false;
  outer: for (let i = 0; i + run.length <= seq.length; i++) {
    for (let k = 0; k < run.length; k++) if (seq[i + k] !== run[k]) continue outer;
    return true;
  }
  return false;
}

/**
 * Dos correcciones DISTINTAS que terminan en la MISMA frase son una sola tarjeta
 * (Emmanuel, 20/10: «we use the train» y «we lose the train», de la misma charla,
 * las dos → «we missed the train», le salieron una atrás de la otra). También
 * dos que son CASI la misma (card #595, Emmanuel 21/10: «I'm learning to play
 * guitar for a month.» y «I've been learning to play guitar for a month now.»,
 * de dos turnos de la misma charla, en el mismo repaso): se agrupan por
 * {@link nearPhraseKey}. En el orden de la primera de cada grupo; de cada grupo
 * se repasa la más larga (la que abarca a las otras; si empatan, la primera) y
 * lo decidido vale para todas. Vale en los cuatro repasos: el del día, el del
 * cierre, el de la partida y el del Radar.
 */
export function groupSamePhrase<T>(items: readonly T[], phraseOf: (item: T) => string): T[][] {
  const groups = new Map<string, T[]>();
  items.forEach((item, i) => {
    // Sin palabras no hay frase que comparar: queda sola.
    const key = nearPhraseKey(phraseOf(item)) || `\u0000${i}`;
    const g = groups.get(key);
    if (g) g.push(item);
    else groups.set(key, [item]);
  });
  return [...groups.values()].map((g) => {
    const size = (item: T): number => assistedTokens(phraseOf(item)).length;
    const keep = g.reduce((best, item) => (size(item) > size(best) ? item : best));
    return [keep, ...g.filter((item) => item !== keep)];
  });
}

/** La frase como la compara la vara (sin mayúsculas, puntuación ni contracciones). */
export function samePhraseKey(phrase: string): string {
  return assistedTokens(phrase).join(' ');
}

/**
 * Las palabras de armado: artículos, auxiliares, preposiciones y adverbios de
 * tiempo cortos. Dos frases que sólo difieren en éstas dicen lo mismo con otro
 * armado («I'm learning … for a month» / «I've been learning … for a month
 * now»). «not» no está: cambia el sentido.
 */
const FRAME_WORDS = new Set([
  'a', 'an', 'the', 'some', 'any', 'that',
  'am', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
  'have', 'has', 'had', 'having', 'do', 'does', 'did',
  'will', 'would', 'shall', 'should', 'can', 'could', 'may', 'might', 'must',
  'to', 'for', 'of', 'in', 'on', 'at', 'by', 'with', 'from', 'about', 'since', 'into',
  'now', 'already', 'just', 'still', 'yet', 'ever', 'really', 'very', 'so', 'too', 'also',
]); // prettier-ignore
/**
 * Con menos palabras de contenido no se compara por el contenido: «I have a
 * dog» y «I had a dog» quedan en «i dog», y ésas sí son frases distintas.
 */
const NEAR_MIN_CONTENT = 3;

/**
 * La clave de {@link groupSamePhrase}: las palabras con contenido de la frase, en
 * orden (sin {@link FRAME_WORDS}). Dos frases con la misma clave son casi la
 * misma. Con menos de {@link NEAR_MIN_CONTENT}, la frase entera
 * ({@link samePhraseKey}); `''` = sin palabras.
 */
export function nearPhraseKey(phrase: string): string {
  const tokens = assistedTokens(phrase);
  const content = tokens.filter((t) => !FRAME_WORDS.has(t));
  return content.length >= NEAR_MIN_CONTENT ? `~${content.join(' ')}` : tokens.join(' ');
}
